import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';
import UndoIcon from '@mui/icons-material/Undo';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { formatDate } from '../i18n/format';
import { useBrand } from '../contexts/BrandContext';
import { tasksApi } from '../services/tasks';
import type {
  Task,
  TaskCategory,
  TaskGuide,
  TaskGuideArtifact,
  TaskPersonaTab,
  TaskSourceType,
  TaskStatus,
} from '@traaker/shared';

type EffortBucket = 'quick' | 'medium' | 'complex';
type TimeFilter = 'all' | '7d' | '30d' | '90d';
type StatusFilter = TaskStatus | 'all';
type PersonaFilter = TaskPersonaTab | 'all';
type SourceFilter = TaskSourceType | 'all';
type PriorityFilter = Task['priority'] | 'all';

const PRIORITY_TO_IMPACT: Record<Task['priority'], number> = {
  critical: 95,
  high: 78,
  medium: 55,
  low: 30,
};

const PRIORITY_LABELS: Record<Task['priority'], string> = {
  critical: 'tasks.priority.critical',
  high: 'tasks.priority.high',
  medium: 'tasks.priority.medium',
  low: 'tasks.priority.low',
};

const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'tasks.status.todo',
  in_progress: 'tasks.status.inProgress',
  done: 'tasks.status.done',
  dismissed: 'tasks.status.dismissed',
};

const STATUS_COLORS: Record<TaskStatus, { fg: string; bg: string }> = {
  todo: { fg: '#5F6470', bg: 'rgba(95, 100, 112, 0.10)' },
  in_progress: { fg: '#2563EB', bg: 'rgba(37, 99, 235, 0.10)' },
  done: { fg: '#059669', bg: 'rgba(5, 150, 105, 0.10)' },
  dismissed: { fg: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.10)' },
};

const EFFORT_LABELS: Record<EffortBucket, string> = {
  quick: 'tasks.effort.quick',
  medium: 'tasks.effort.medium',
  complex: 'tasks.effort.complex',
};

const EFFORT_COLORS: Record<EffortBucket, { fg: string; bg: string }> = {
  quick: { fg: '#059669', bg: 'rgba(5, 150, 105, 0.12)' },
  medium: { fg: '#D97706', bg: 'rgba(217, 119, 6, 0.12)' },
  complex: { fg: '#DC2626', bg: 'rgba(220, 38, 38, 0.12)' },
};

const PERSONA_LABELS: Record<TaskPersonaTab, string> = {
  technique: 'tasks.persona.technique',
  contenu: 'tasks.persona.contenu',
  hors_site: 'tasks.persona.horsSite',
};

const PERSONA_COLORS: Record<TaskPersonaTab, { fg: string; bg: string }> = {
  technique: { fg: '#4F46E5', bg: 'rgba(79, 70, 229, 0.10)' },
  contenu: { fg: '#7C3AED', bg: 'rgba(124, 58, 237, 0.10)' },
  hors_site: { fg: '#0891B2', bg: 'rgba(8, 145, 178, 0.10)' },
};

const CATEGORY_LABELS: Record<TaskCategory, string> = {
  technical: 'tasks.category.technical',
  schema: 'tasks.category.schema',
  seo: 'tasks.category.seo',
  content: 'tasks.category.content',
  external: 'tasks.category.external',
  earned_media: 'tasks.category.earnedMedia',
  social: 'tasks.category.social',
};

const CATEGORY_COLORS: Record<TaskCategory, { fg: string; bg: string }> = {
  technical: { fg: '#0F766E', bg: 'rgba(15, 118, 110, 0.10)' },
  schema: { fg: '#0EA5E9', bg: 'rgba(14, 165, 233, 0.10)' },
  seo: { fg: '#2563EB', bg: 'rgba(37, 99, 235, 0.10)' },
  content: { fg: '#7C3AED', bg: 'rgba(124, 58, 237, 0.10)' },
  external: { fg: '#DB2777', bg: 'rgba(219, 39, 119, 0.10)' },
  earned_media: { fg: '#EA580C', bg: 'rgba(234, 88, 12, 0.10)' },
  social: { fg: '#9333EA', bg: 'rgba(147, 51, 234, 0.10)' },
};

const SOURCE_LABELS: Record<TaskSourceType, string> = {
  audit_website: 'tasks.source.website',
  audit_brand: 'tasks.source.brand',
  audit_earned_media: 'tasks.source.earnedMedia',
  manual: 'tasks.source.manual',
};

const VERIFICATION_COLORS: Record<string, string> = {
  verified: '#059669',
  not_fixed: '#DC2626',
  inconclusive: '#D97706',
  pending: '#6B7280',
};

const VERIFICATION_LABELS: Record<string, string> = {
  verified: 'tasks.verification.verified',
  not_fixed: 'tasks.verification.notFixed',
  inconclusive: 'tasks.verification.inconclusive',
  pending: 'tasks.verification.pending',
};

function bucketEffort(raw?: string): EffortBucket {
  if (!raw) return 'medium';
  const s = raw.toLowerCase();
  if (/min|≤\s*1h|< ?1h|^~?1h$|quick/.test(s)) return 'quick';
  if (/jour|day|developpeur|développeur|semaine|week|complex/.test(s)) return 'complex';
  return 'medium';
}

function impactOf(task: Task): number {
  return PRIORITY_TO_IMPACT[task.priority];
}

function isQuickWin(task: Task): boolean {
  return impactOf(task) >= 75 && bucketEffort(task.effortEstimate) === 'quick';
}

function withinTimeFilter(task: Task, filter: TimeFilter): boolean {
  if (filter === 'all') return true;
  const days = filter === '7d' ? 7 : filter === '30d' ? 30 : 90;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(task.createdAt).getTime() >= cutoff;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Split a markdown table row into trimmed cell strings (drops leading/trailing pipes). */
function tableCells(row: string): string[] {
  return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

function isSeparatorRow(row: string): boolean {
  const cells = tableCells(row);
  return cells.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c.replace(/\s/g, '')));
}

/**
 * Extract GitHub-flavoured markdown tables and replace each with a `\x00TB<i>\x00`
 * placeholder (stashed HTML). Tolerates a header row glued to preceding prose
 * (the model sometimes writes "... : | A | B |") by using the separator row's
 * column count and taking the trailing N cells as the header — the prose prefix
 * is preserved as its own line before the table.
 */
function extractTables(text: string, store: string[]): string {
  const lines = text.split('\n');
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const next = lines[i + 1];
    if (lines[i].includes('|') && next !== undefined && isSeparatorRow(next)) {
      const colCount = tableCells(next).length;
      const rawHeader = tableCells(lines[i]);
      const header = rawHeader.slice(-colCount);
      const prefix = rawHeader.length > colCount
        ? rawHeader.slice(0, rawHeader.length - colCount).join(' | ').trim()
        : '';
      const bodyRows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|') && lines[j].trim() !== '') {
        bodyRows.push(tableCells(lines[j]));
        j++;
      }
      const thead = `<thead><tr>${header
        .map((c) => `<th style="text-align:left;padding:6px 10px;border:1px solid rgba(124,58,237,0.2);background:rgba(124,58,237,0.06);font-weight:700">${escapeHtml(c)}</th>`)
        .join('')}</tr></thead>`;
      const tbody = `<tbody>${bodyRows
        .map((r) => `<tr>${Array.from({ length: colCount }, (_, k) => `<td style="padding:6px 10px;border:1px solid rgba(124,58,237,0.15)">${escapeHtml(r[k] || '')}</td>`).join('')}</tr>`)
        .join('')}</tbody>`;
      store.push(`<table style="border-collapse:collapse;width:100%;margin:10px 0;font-size:0.8rem">${thead}${tbody}</table>`);
      if (prefix) out.push(prefix);
      out.push(`\x00TB${store.length - 1}\x00`);
      i = j - 1;
    } else {
      out.push(lines[i]);
    }
  }
  return out.join('\n');
}

function simpleMarkdownToHtml(md: string): string {
  const codeBlocks: string[] = [];
  const inlineCodes: string[] = [];
  const tables: string[] = [];

  let result = md
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_, _lang, code) => {
      codeBlocks.push(`<pre style="background:rgba(0,0,0,0.15);padding:12px;border-radius:4px;overflow-x:auto;font-size:13px"><code>${escapeHtml(code)}</code></pre>`);
      return `\x00CB${codeBlocks.length - 1}\x00`;
    })
    .replace(/`([^`]+)`/g, (_, code) => {
      inlineCodes.push(`<code style="background:rgba(124,58,237,0.10);padding:1px 4px;border-radius:3px;font-size:13px">${escapeHtml(code)}</code>`);
      return `\x00IC${inlineCodes.length - 1}\x00`;
    });

  result = extractTables(result, tables);

  result = result
    .replace(/^####\s+(.+)$/gm, '<h4 style="margin:16px 0 8px;font-size:14px;font-weight:700;color:#7C3AED">$1</h4>')
    .replace(/^###\s+(.+)$/gm, '<h3 style="margin:16px 0 8px;font-size:15px;font-weight:700;color:#7C3AED">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^[\s]*[-*]\s+(.+)$/gm, '<li style="margin:2px 0">$1</li>')
    .replace(/^[\s]*(\d+)\.\s+(.+)$/gm, '<li style="margin:4px 0" value="$1">$2</li>')
    .replace(/((?:<li[^>]*>.*<\/li>\s*)+)/g, '<ul style="margin:4px 0 12px 16px;padding:0;list-style:disc">$1</ul>')
    .replace(/^(?!<[a-z])(?!\x00)((?!^\s*$).+)$/gm, '<p style="margin:4px 0;line-height:1.6">$1</p>')
    .replace(/<p[^>]*>\s*<\/p>/g, '');

  result = result.replace(/\x00CB(\d+)\x00/g, (_, i) => codeBlocks[+i]);
  result = result.replace(/\x00IC(\d+)\x00/g, (_, i) => inlineCodes[+i]);
  result = result.replace(/\x00TB(\d+)\x00/g, (_, i) => tables[+i]);
  return result;
}

// --- Structured guide rendering (stepper + SVG workflow + artifacts) ---

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={copied ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch {
          /* clipboard unavailable — no-op */
        }
      }}
      sx={{ height: 26, fontSize: '0.7rem', textTransform: 'none', borderColor: 'rgba(124,58,237,0.4)', color: '#7C3AED' }}
    >
      {copied ? t('tasks.copied') : t('tasks.copy')}
    </Button>
  );
}

/** Compact inline SVG of the steps as a connected node chain. */
function WorkflowDiagram({ steps }: { steps: TaskGuide['steps'] }) {
  const { t } = useTranslation();
  if (steps.length < 2) return null;
  const n = steps.length;
  const gap = 132;
  const r = 16;
  const width = (n - 1) * gap + 2 * r + 16;
  const cy = 22;
  const x = (i: number) => r + 8 + i * gap;
  return (
    <Box sx={{ overflowX: 'auto', mb: 2 }}>
      <svg width={width} height={66} role="img" aria-label={t('tasks.guide.stepsAria')}>
        {steps.slice(0, -1).map((_, i) => (
          <line
            key={`l${i}`}
            x1={x(i) + r}
            y1={cy}
            x2={x(i + 1) - r}
            y2={cy}
            stroke="rgba(124,58,237,0.35)"
            strokeWidth={2}
          />
        ))}
        {steps.map((s, i) => (
          <g key={`n${i}`}>
            <circle cx={x(i)} cy={cy} r={r} fill="#7C3AED" />
            <text x={x(i)} y={cy + 4} textAnchor="middle" fontSize={13} fontWeight={700} fill="#fff">
              {i + 1}
            </text>
            <text x={x(i)} y={cy + r + 16} textAnchor="middle" fontSize={10} fill="#64748B">
              {s.title.length > 18 ? s.title.slice(0, 17) + '…' : s.title}
            </text>
          </g>
        ))}
      </svg>
    </Box>
  );
}

function ArtifactCard({ artifact }: { artifact: TaskGuideArtifact }) {
  return (
    <Box sx={{ mb: 1.5, border: '1px solid rgba(124,58,237,0.18)', borderRadius: 1.5, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 0.75, bgcolor: 'rgba(124,58,237,0.06)' }}
      >
        <Typography variant="caption" fontWeight={700} sx={{ color: '#7C3AED' }}>
          {artifact.label}
          {artifact.language ? ` · ${artifact.language}` : ''}
        </Typography>
        <CopyButton text={artifact.content} />
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          fontSize: '0.78rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          bgcolor: 'rgba(0,0,0,0.03)',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}
      >
        {artifact.content}
      </Box>
    </Box>
  );
}

function GuideView({ guide }: { guide: TaskGuide }) {
  const { t } = useTranslation();
  return (
    <Box>
      <WorkflowDiagram steps={guide.steps} />
      <Stepper orientation="vertical" nonLinear activeStep={-1} sx={{ '& .MuiStepLabel-label': { fontWeight: 700, fontSize: '0.9rem' } }}>
        {guide.steps.map((s, i) => (
          <Step key={i} active expanded>
            <StepLabel>{s.title}</StepLabel>
            <StepContent>
              <Box
                sx={{ fontSize: '0.85rem', lineHeight: 1.6, '& pre': { my: 1 } }}
                dangerouslySetInnerHTML={{ __html: simpleMarkdownToHtml(s.detail) }}
              />
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {guide.artifacts && guide.artifacts.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#7C3AED' }}>
            {t('tasks.guide.deliverables')}
          </Typography>
          {guide.artifacts.map((a, i) => (
            <ArtifactCard key={i} artifact={a} />
          ))}
        </Box>
      )}

      {guide.references && guide.references.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#7C3AED' }}>
            {t('tasks.guide.sources')}
          </Typography>
          <Stack spacing={0.5}>
            {guide.references.map((ref, i) => (
              <Typography
                key={i}
                variant="body2"
                component="a"
                href={ref.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#7C3AED', display: 'inline-flex', alignItems: 'center', gap: 0.5, wordBreak: 'break-all' }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
                {ref.title}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

/** Escape for embedding text into the self-contained HTML export. */
function escForHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Build a standalone, shareable HTML file from a task's guide. */
function buildGuideHtml(task: Task): string {
  const guide = task.guide;
  const steps = (guide?.steps || [])
    .map(
      (s) =>
        `<li><h3>${escForHtml(s.title)}</h3><div>${simpleMarkdownToHtml(s.detail)}</div></li>`,
    )
    .join('');
  const artifacts = (guide?.artifacts || [])
    .map(
      (a, i) => `<div class="artifact">
      <div class="artifact-head"><strong>${escForHtml(a.label)}${a.language ? ' · ' + escForHtml(a.language) : ''}</strong>
      <button onclick="copyArtifact(${i})">${escForHtml(i18n.t('tasks.copy'))}</button></div>
      <pre id="artifact-${i}">${escForHtml(a.content)}</pre></div>`,
    )
    .join('');
  const references = (guide?.references || [])
    .map((r) => `<li><a href="${escForHtml(r.url)}" target="_blank" rel="noopener">${escForHtml(r.title)}</a></li>`)
    .join('');

  return `<!DOCTYPE html>
<html lang="fr"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escForHtml(i18n.t('tasks.export.title', { title: task.title }))}</title>
<style>
  body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#1e293b;max-width:760px;margin:0 auto;padding:32px 20px;line-height:1.6}
  h1{font-size:22px;color:#7C3AED;margin-bottom:4px}
  .desc{color:#475569;margin:0 0 24px}
  ol{padding-left:0;list-style:none;counter-reset:step}
  ol>li{position:relative;padding:0 0 18px 44px;border-left:2px solid rgba(124,58,237,.25);margin-left:14px}
  ol>li::before{counter-increment:step;content:counter(step);position:absolute;left:-15px;top:0;width:28px;height:28px;border-radius:50%;background:#7C3AED;color:#fff;font-weight:700;display:flex;align-items:center;justify-content:center;font-size:14px}
  ol>li h3{margin:0 0 4px;font-size:15px}
  pre{background:#f1f5f9;padding:12px;border-radius:6px;overflow-x:auto;font-size:13px;white-space:pre-wrap;word-break:break-word}
  code{background:rgba(124,58,237,.1);padding:1px 4px;border-radius:3px;font-size:13px}
  .artifact{border:1px solid rgba(124,58,237,.2);border-radius:8px;margin:12px 0;overflow:hidden}
  .artifact-head{display:flex;justify-content:space-between;align-items:center;background:rgba(124,58,237,.06);padding:8px 12px}
  .artifact-head button{background:#7C3AED;color:#fff;border:0;border-radius:5px;padding:5px 12px;cursor:pointer;font-size:12px}
  .artifact pre{margin:0;border-radius:0}
  h2{font-size:16px;color:#7C3AED;margin-top:28px}
  a{color:#7C3AED}
  footer{margin-top:40px;color:#94a3b8;font-size:12px;border-top:1px solid #e2e8f0;padding-top:12px}
</style></head><body>
<h1>${escForHtml(task.title)}</h1>
<p class="desc">${escForHtml(task.description)}</p>
${task.sourcePageUrl ? `<p class="desc">${escForHtml(i18n.t('tasks.export.page'))} : <a href="${escForHtml(task.sourcePageUrl)}" target="_blank" rel="noopener">${escForHtml(task.sourcePageUrl)}</a></p>` : ''}
<h2>${escForHtml(i18n.t('tasks.export.steps'))}</h2>
<ol>${steps}</ol>
${artifacts ? `<h2>${escForHtml(i18n.t('tasks.guide.deliverables'))}</h2>${artifacts}` : ''}
${references ? `<h2>${escForHtml(i18n.t('tasks.guide.sources'))}</h2><ul>${references}</ul>` : ''}
<footer>${escForHtml(i18n.t('tasks.export.footer', { date: formatDate(new Date()) }))}</footer>
<script>
function copyArtifact(i){var el=document.getElementById('artifact-'+i);navigator.clipboard.writeText(el.textContent).then(function(){var b=el.parentNode.querySelector('button');var t=b.textContent;b.textContent=${JSON.stringify(i18n.t('tasks.export.copiedCheck'))};setTimeout(function(){b.textContent=t;},1800);});}
</script>
</body></html>`;
}

function downloadGuide(task: Task) {
  const html = buildGuideHtml(task);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const slug = task.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50) || 'guide';
  a.href = url;
  a.download = `guide-${slug}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Tasks() {
  const { t } = useTranslation();
  const { activeBrand } = useBrand();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ text: string; severity: 'error' | 'warning' | 'success' } | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [personaFilter, setPersonaFilter] = useState<PersonaFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadTasks = useCallback(async () => {
    try {
      const data = await tasksApi.list(activeBrand?.brandId);
      setTasks(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.brandId]);

  useEffect(() => {
    setLoading(true);
    loadTasks();
  }, [loadTasks]);

  // Deep-link: `/tasks?openTask=<id>` opens the task detail dialog once tasks
  // are loaded (used by the dashboard CTA cards). Clears the param after.
  useEffect(() => {
    const openId = searchParams.get('openTask');
    if (!openId || tasks.length === 0) return;
    const found = tasks.find((t) => t.taskId === openId);
    if (found) {
      setDetailTask(found);
      searchParams.delete('openTask');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, tasks, setSearchParams]);

  const handleStatusChange = async (taskId: string, status: TaskStatus) => {
    try {
      const updated = await tasksApi.updateStatus(taskId, status);
      setTasks((prev) => prev.map((t) => (t.taskId === taskId ? updated : t)));
      if (status === 'done') setFeedback({ text: t('tasks.feedback.taskDone'), severity: 'success' });
    } catch {
      setFeedback({ text: t('tasks.feedback.updateError'), severity: 'error' });
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await tasksApi.delete(taskId);
      setTasks((prev) => prev.filter((t) => t.taskId !== taskId));
      if (detailTask?.taskId === taskId) setDetailTask(null);
    } catch {
      setFeedback({ text: t('tasks.feedback.deleteError'), severity: 'error' });
    }
  };

  const handleVerify = async (taskId: string) => {
    setFeedback({ text: t('tasks.feedback.verifying'), severity: 'success' });
    try {
      const updated = await tasksApi.verify(taskId);
      setTasks((prev) => prev.map((t) => (t.taskId === taskId ? updated : t)));
      setFeedback(
        updated.verificationStatus === 'verified'
          ? { text: t('tasks.feedback.verified'), severity: 'success' }
          : updated.verificationStatus === 'not_fixed'
            ? { text: t('tasks.feedback.notFixed'), severity: 'warning' }
            : { text: t('tasks.feedback.inconclusive'), severity: 'success' },
      );
    } catch {
      setFeedback({ text: t('tasks.feedback.verifyError'), severity: 'error' });
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks
      .filter((t) => statusFilter === 'all' || t.status === statusFilter)
      .filter((t) => personaFilter === 'all' || t.personaTab === personaFilter)
      .filter((t) => sourceFilter === 'all' || t.sourceType === sourceFilter)
      .filter((t) => priorityFilter === 'all' || t.priority === priorityFilter)
      .filter((t) => withinTimeFilter(t, timeFilter))
      .filter((t) => {
        if (!q) return true;
        return (
          t.title.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          (t.sourcePageUrl ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => impactOf(b) - impactOf(a));
  }, [tasks, search, statusFilter, personaFilter, sourceFilter, priorityFilter, timeFilter]);

  const kpis = useMemo(() => {
    const quickWins = tasks.filter(isQuickWin).length;
    return {
      total: tasks.length,
      quickWins,
      pending: tasks.filter((t) => t.status === 'todo').length,
      inProgress: tasks.filter((t) => t.status === 'in_progress').length,
      done: tasks.filter((t) => t.status === 'done').length,
    };
  }, [tasks]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('tasks.pageTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('tasks.pageSubtitle')}
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.text}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard label={t('tasks.kpi.total')} value={kpis.total} color="#111827" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard
            label={t('tasks.kpi.quickWins')}
            value={kpis.quickWins}
            color="#D97706"
            icon={<BoltIcon sx={{ fontSize: 18, color: '#F59E0B' }} />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard label={t('tasks.kpi.pending')} value={kpis.pending} color="#EA580C" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard label={t('tasks.kpi.inProgress')} value={kpis.inProgress} color="#2563EB" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard label={t('tasks.kpi.completed')} value={kpis.done} color="#059669" />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems="stretch">
            <TextField
              size="small"
              placeholder={t('tasks.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              allLabel={t('tasks.filter.allStatuses')}
              options={[
                ['todo', t('tasks.status.todo')],
                ['in_progress', t('tasks.status.inProgress')],
                ['done', t('tasks.status.done')],
                ['dismissed', t('tasks.status.dismissed')],
              ]}
            />
            <FilterSelect
              value={personaFilter}
              onChange={setPersonaFilter}
              allLabel={t('tasks.filter.allTypes')}
              options={[
                ['technique', t('tasks.persona.technique')],
                ['contenu', t('tasks.persona.contenu')],
                ['hors_site', t('tasks.persona.horsSite')],
              ]}
            />
            <FilterSelect
              value={sourceFilter}
              onChange={setSourceFilter}
              allLabel={t('tasks.filter.allSources')}
              options={[
                ['audit_website', t('tasks.source.website')],
                ['audit_brand', t('tasks.source.brand')],
                ['audit_earned_media', t('tasks.source.earnedMedia')],
                ['manual', t('tasks.source.manual')],
              ]}
            />
            <FilterSelect
              value={priorityFilter}
              onChange={setPriorityFilter}
              allLabel={t('tasks.filter.allPriorities')}
              options={[
                ['critical', t('tasks.priority.critical')],
                ['high', t('tasks.priority.high')],
                ['medium', t('tasks.priority.medium')],
                ['low', t('tasks.priority.low')],
              ]}
            />
            <FilterSelect
              value={timeFilter}
              onChange={setTimeFilter}
              allLabel={t('tasks.filter.allTime')}
              options={[
                ['7d', t('tasks.filter.last7d')],
                ['30d', t('tasks.filter.last30d')],
                ['90d', t('tasks.filter.last90d')],
              ]}
            />
          </Stack>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('tasks.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tasks.length === 0
                ? t('tasks.empty.noTasks')
                : t('tasks.empty.noMatch')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TaskTable
          tasks={filtered}
          onOpenDetail={setDetailTask}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onVerify={handleVerify}
        />
      )}

      {detailTask && (
        <TaskDetailDialog
          task={detailTask}
          open
          onClose={() => setDetailTask(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onVerify={handleVerify}
        />
      )}
    </Box>
  );
}

function KpiCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number;
  color: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          {icon}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ color, lineHeight: 1.1 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function FilterSelect<T extends string>({
  value,
  onChange,
  allLabel,
  options,
}: {
  value: T | 'all';
  onChange: (v: T | 'all') => void;
  allLabel: string;
  options: [T, string][];
}) {
  return (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value as T | 'all')}
      sx={{ minWidth: 150, bgcolor: 'background.paper' }}
    >
      <MenuItem value="all">{allLabel}</MenuItem>
      {options.map(([k, label]) => (
        <MenuItem key={k} value={k}>
          {label}
        </MenuItem>
      ))}
    </Select>
  );
}

function TaskTable({
  tasks,
  onOpenDetail,
  onStatusChange,
  onDelete,
  onVerify,
}: {
  tasks: Task[];
  onOpenDetail: (t: Task) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
  onVerify: (taskId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.default' }}>
            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {t('tasks.col.recommendation')}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, width: 110 }}>
              {t('tasks.col.impact')}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, width: 110 }}>
              {t('tasks.col.effort')}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, width: 130 }}>
              {t('tasks.col.status')}
            </TableCell>
            <TableCell align="right" sx={{ width: 130 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.map((task) => (
            <TaskRow
              key={task.taskId}
              task={task}
              onOpenDetail={onOpenDetail}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
              onVerify={onVerify}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TaskRow({
  task,
  onOpenDetail,
  onStatusChange,
  onDelete,
  onVerify,
}: {
  task: Task;
  onOpenDetail: (t: Task) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
  onVerify: (taskId: string) => void;
}) {
  const { t } = useTranslation();
  const impact = impactOf(task);
  const effort = bucketEffort(task.effortEstimate);
  const quick = isQuickWin(task);
  const persona = PERSONA_COLORS[task.personaTab];
  const category = CATEGORY_COLORS[task.category];

  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
      onClick={() => onOpenDetail(task)}
    >
      <TableCell sx={{ py: 1.25 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
          {task.title}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <TagChip label={t(PERSONA_LABELS[task.personaTab])} colors={persona} />
          <TagChip label={t(CATEGORY_LABELS[task.category])} colors={category} />
          {task.originLabel && (
            <Chip
              label={task.originLabel}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary' }}
            />
          )}
          {task.verificationStatus && task.verificationStatus !== 'pending' && (
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 12 }} />}
              label={t(VERIFICATION_LABELS[task.verificationStatus])}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: `${VERIFICATION_COLORS[task.verificationStatus]}1A`,
                color: VERIFICATION_COLORS[task.verificationStatus],
                '& .MuiChip-icon': { color: VERIFICATION_COLORS[task.verificationStatus] },
              }}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <ImpactGauge value={impact} />
          {quick && (
            <Tooltip title={t('tasks.quickWin')}>
              <BoltIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={t(EFFORT_LABELS[effort])}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: EFFORT_COLORS[effort].bg,
            color: EFFORT_COLORS[effort].fg,
            border: 'none',
          }}
        />
      </TableCell>
      <TableCell align="center">
        <StatusPill status={task.status} />
      </TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <RowActions
          task={task}
          onStatusChange={onStatusChange}
          onDelete={onDelete}
          onVerify={onVerify}
        />
      </TableCell>
    </TableRow>
  );
}

function TagChip({ label, colors }: { label: string; colors: { fg: string; bg: string } }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 600,
        bgcolor: colors.bg,
        color: colors.fg,
        border: 'none',
        '& .MuiChip-label': { px: 0.875 },
      }}
    />
  );
}

function ImpactGauge({ value }: { value: number }) {
  const theme = useTheme();
  const stroke =
    value >= 80 ? '#10B981' : value >= 60 ? '#7C3AED' : value >= 40 ? '#F59E0B' : '#9CA3AF';
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - value / 100);

  return (
    <Box sx={{ position: 'relative', width: 40, height: 40, display: 'inline-block' }}>
      <svg width={40} height={40} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={20}
          cy={20}
          r={radius}
          fill="none"
          stroke={theme.palette.divider}
          strokeWidth={3}
        />
        <circle
          cx={20}
          cy={20}
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth={3}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: stroke }}>{value}</Typography>
      </Box>
    </Box>
  );
}

function StatusPill({ status }: { status: TaskStatus }) {
  const { t } = useTranslation();
  const c = STATUS_COLORS[status];
  const Icon =
    status === 'in_progress'
      ? PlayArrowIcon
      : status === 'done'
        ? CheckCircleIcon
        : status === 'dismissed'
          ? BlockIcon
          : ScheduleIcon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 14 }} />}
      label={t(STATUS_LABELS[status])}
      size="small"
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        bgcolor: c.bg,
        color: c.fg,
        border: 'none',
        '& .MuiChip-icon': { color: c.fg, ml: '6px' },
      }}
    />
  );
}

function RowActions({
  task,
  onStatusChange,
  onDelete,
  onVerify,
}: {
  task: Task;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
  onVerify: (taskId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={0} justifyContent="flex-end">
      {task.status === 'todo' && (
        <Tooltip title={t('tasks.action.start')}>
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'in_progress')}>
            <PlayArrowIcon fontSize="small" sx={{ color: '#2563EB' }} />
          </IconButton>
        </Tooltip>
      )}
      {task.status === 'in_progress' && (
        <Tooltip title={t('tasks.action.finish')}>
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'done')}>
            <CheckCircleIcon fontSize="small" sx={{ color: '#059669' }} />
          </IconButton>
        </Tooltip>
      )}
      {task.status === 'done' && task.sourcePageUrl && task.personaTab === 'technique' && (
        <Tooltip title={t('tasks.action.verifyFix')}>
          <IconButton size="small" onClick={() => onVerify(task.taskId)}>
            <VerifiedIcon fontSize="small" sx={{ color: '#7C3AED' }} />
          </IconButton>
        </Tooltip>
      )}
      {task.status === 'done' && (
        <Tooltip title={t('tasks.action.reopen')}>
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'todo')}>
            <UndoIcon fontSize="small" sx={{ color: '#EA580C' }} />
          </IconButton>
        </Tooltip>
      )}
      {(task.status === 'todo' || task.status === 'in_progress') && (
        <Tooltip title={t('tasks.action.dismiss')}>
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'dismissed')}>
            <BlockIcon fontSize="small" sx={{ color: '#6B7280' }} />
          </IconButton>
        </Tooltip>
      )}
      {task.status === 'dismissed' && (
        <Tooltip title={t('tasks.action.reopen')}>
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'todo')}>
            <UndoIcon fontSize="small" sx={{ color: '#7C3AED' }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t('tasks.action.delete')}>
        <IconButton size="small" onClick={() => onDelete(task.taskId)}>
          <DeleteOutlineIcon fontSize="small" sx={{ color: '#DC2626' }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function TaskDetailDialog({
  task: taskProp,
  open,
  onClose,
  onStatusChange,
  onDelete,
  onVerify,
}: {
  task: Task;
  open: boolean;
  onClose: () => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
  onVerify: (taskId: string) => void;
}) {
  // Tasks are created instantly WITHOUT a guide (guideStatus:'pending'); the
  // rich "Comment faire ?" guide is generated lazily the first time the detail
  // dialog opens. `task` is local state so the freshly-generated guide renders
  // without a parent refetch.
  const { t } = useTranslation();
  const [task, setTask] = useState<Task>(taskProp);
  const [guideLoading, setGuideLoading] = useState(false);
  useEffect(() => setTask(taskProp), [taskProp]);

  const needsGuide =
    !!task.guideStatus && task.guideStatus !== 'ready' && !(task.guide?.steps?.length);

  useEffect(() => {
    if (!open) return;
    // Legacy tasks (no guideStatus) already have a guide — skip.
    if (!needsGuide || guideLoading) return;
    let cancelled = false;
    setGuideLoading(true);
    tasksApi
      .generateGuide(task.taskId)
      .then((updated) => {
        if (!cancelled) setTask(updated);
      })
      .catch(() => {
        /* leave guideStatus as-is; the retry button handles it */
      })
      .finally(() => {
        if (!cancelled) setGuideLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task.taskId]);

  const retryGuide = () => {
    setGuideLoading(true);
    tasksApi
      .generateGuide(task.taskId)
      .then(setTask)
      .catch(() => {})
      .finally(() => setGuideLoading(false));
  };

  const impact = impactOf(task);
  const effort = bucketEffort(task.effortEstimate);
  const quick = isQuickWin(task);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
        <ImpactGauge value={impact} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {task.title}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
            <TagChip label={t(PERSONA_LABELS[task.personaTab])} colors={PERSONA_COLORS[task.personaTab]} />
            <TagChip label={t(CATEGORY_LABELS[task.category])} colors={CATEGORY_COLORS[task.category]} />
            <Chip
              label={t(EFFORT_LABELS[effort])}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: EFFORT_COLORS[effort].bg,
                color: EFFORT_COLORS[effort].fg,
              }}
            />
            <Chip
              label={t('tasks.priorityLabel', { priority: t(PRIORITY_LABELS[task.priority]) })}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
            />
            {quick && (
              <Chip
                icon={<BoltIcon sx={{ fontSize: 12 }} />}
                label={t('tasks.quickWin')}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                  color: '#D97706',
                  '& .MuiChip-icon': { color: '#F59E0B' },
                }}
              />
            )}
          </Stack>
        </Box>
        <StatusPill status={task.status} />
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {task.description}
        </Typography>

        {task.sourcePageUrl && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">{t('tasks.detail.sourcePage')}</Typography>
            <Typography
              variant="body2"
              sx={{ color: '#7C3AED', cursor: 'pointer', wordBreak: 'break-all' }}
              onClick={() => window.open(task.sourcePageUrl, '_blank')}
            >
              {task.sourcePageUrl}
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">{t('tasks.detail.provenance')}</Typography>
          <Typography variant="body2">
            {task.originLabel
              ? `${t(SOURCE_LABELS[task.sourceType])} · ${task.originLabel}`
              : t(SOURCE_LABELS[task.sourceType])}
          </Typography>
        </Box>

        {(task.guide?.steps?.length || task.howTo || needsGuide || guideLoading) && (
          <Box sx={{ bgcolor: 'rgba(124,58,237,0.04)', borderRadius: 1.5, p: 2, border: '1px solid rgba(124,58,237,0.18)' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#7C3AED' }}>
              {t('tasks.detail.howTo')}
            </Typography>
            {task.guide?.steps?.length ? (
              <GuideView guide={task.guide} />
            ) : task.howTo ? (
              <Box
                sx={{ fontSize: '0.875rem', lineHeight: 1.6, '& pre': { my: 1 }, '& h3, & h4': { '&:first-of-type': { mt: 0 } } }}
                dangerouslySetInnerHTML={{ __html: simpleMarkdownToHtml(task.howTo!) }}
              />
            ) : guideLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <CircularProgress size={18} sx={{ color: '#7C3AED' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('tasks.detail.generatingGuide')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('tasks.detail.guideFailed')}
                </Typography>
                <Button size="small" onClick={retryGuide} sx={{ textTransform: 'none' }}>
                  {t('tasks.detail.retry')}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {task.verificationDetail && (
          <Box sx={{ mt: 2, bgcolor: `${VERIFICATION_COLORS[task.verificationStatus || 'pending']}0F`, borderRadius: 1.5, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {t('tasks.detail.verificationResult')}
            </Typography>
            <Typography variant="body2">{task.verificationDetail}</Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('tasks.detail.createdOn', { date: formatDate(task.createdAt) })}
          </Typography>
          {task.updatedAt !== task.createdAt && (
            <Typography variant="caption" color="text.secondary">
              {t('tasks.detail.updatedOn', { date: formatDate(task.updatedAt) })}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          <RowActions
            task={task}
            onStatusChange={onStatusChange}
            onDelete={onDelete}
            onVerify={onVerify}
          />
        </Box>
        <Stack direction="row" spacing={1}>
          {(task.guide?.steps?.length || task.howTo) && (
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={() => downloadGuide(task)}
              sx={{ textTransform: 'none' }}
            >
              {t('tasks.detail.downloadGuide')}
            </Button>
          )}
          <Button onClick={onClose}>{t('tasks.close')}</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
